// Modules
import { connect } from 'react-redux';
import Promise from 'bluebird';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { GoogleDriveLink } from 'components/widgets/icons/link-icons';
import { Page, PageHeader, PageContent, ActionSection } from 'components/page';
import { PrimaryButton } from 'components/widgets/buttons';
import { SORTABLE_TIME_FORMAT } from 'constants';
import { SortableTH } from 'components/content-containers/tables';
import { Table } from 'components/content-containers/tables/tables';
import { alerts } from 'modules/alerts/actions';
import { didQueryUpdate } from 'utils/filter-utils';
import Agreement from 'services/data/agreement';
import AgreementLink from 'components/models/agreement/agreement-link';
import CompanyNickname from 'components/models/company/company-nickname';
import Filters from 'modules/filters/filters';
import Pager from 'components/widgets/pager/pager';
import ShortDate from 'components/widgets/dates/short-date';
import exportCSV from 'utils/export-csv';

import * as actions from './store/actions';


// Component
class Agreements extends React.Component {
  static propTypes = {
    agreements: PropTypes.array,
    fetching: PropTypes.bool,
    count: PropTypes.number,
    query: PropTypes.object
  };

  constructor() {
    super();

    this.search = this.search.bind(this);
    this.export = this.export.bind(this);

    this.filterConfig = {
      basic: ['company'],
      advanced: [
        ['search', 'company', 'team'],
        ['startDate', 'endDate'],
        ['agreementType']
      ],
      defaults: {
        startDate: { max: moment().startOf('day').toDate() },
        endDate: { min: moment().startOf('day').toDate() }
      }
    };
  }

  componentWillReceiveProps(nextProps) {
    if (didQueryUpdate(nextProps, this.props)) this.search(nextProps.query);
  }

  componentWillUnmount() {
    actions.set({ agreements: [], count: null });
  }

  search(query) {
    actions.set({ fetching: true });

    const fullQuery = Agreement.formatQuery(query || this.props.query);

    return Promise.props({
      agreements: Agreement.find(fullQuery),
      count: Agreement.count({ where: fullQuery.where })
    })
      .then((response) => {
        actions.set({ fetching: false, agreements: response.agreements, count: response.count });
      })
      .catch(alerts.catchError);
  }

  export() {
    const fullQuery = Agreement.formatQuery(this.props.query);
    delete fullQuery.limit;

    Agreement.find(fullQuery)
      .then((agreements) => {
        const header = ['Company', 'Title', 'Start Date', 'End Date', 'Department', 'Billing'];

        const data = _.map(agreements, agreement => {
          const row = [
            _.get(agreement.company, 'name', ''),
            _.get(agreement, 'title', ''),
            agreement.startDate ? moment(agreement.startDate).format(SORTABLE_TIME_FORMAT) : '',
            agreement.endDate ? moment(agreement.endDate).format(SORTABLE_TIME_FORMAT) : '',
            _.get(agreement, 'department', '')
          ];

          if (agreement.monthly) row.push('Monthly');
          if (!agreement.monthly && agreement.timeDefaultBillable) row.push('Hourly');
          if (!agreement.monthly && !agreement.timeDefaultBillable) row.push('Fixed');

          return row;
        });

        exportCSV(data, 'agreements.csv', { header });
      })
      .catch(alerts.catchError);
  }

  render() {
    const { agreements = [], count = 0, fetching } = this.props;

    const headerActions = (
      <div className="header-actions">
        <PrimaryButton onClick={this.export} disabled={_.isEmpty(agreements)}>Export</PrimaryButton>
      </div>
    );

    return (
      <Page>
        <PageHeader title="Agreements" actions={headerActions}>
          <ActionSection>
            <Filters config={this.filterConfig} />
          </ActionSection>
        </PageHeader>
        <PageContent
          fetching={fetching}
          emptyText="No agreements, adjust your filters!"
          emptyTextIcon="svg/agreement.svg"
          showEmptyText={!fetching && _.isEmpty(agreements)}
          className="agreements"
        >
          <div className="companies-table">
            <Table>
              <thead>
                <tr>
                  <SortableTH sortField="company.name">Company</SortableTH>
                  <SortableTH sortField="title">Agreement</SortableTH>
                  <SortableTH sortField="startDate">Start</SortableTH>
                  <SortableTH sortField="endDate">End</SortableTH>
                  <th>Department</th>
                  <th>Billing</th>
                </tr>
              </thead>
              <tbody>
                {_.map(agreements, (agreement) => {
                  const expired = agreement.endDate && moment().startOf('day').isAfter(agreement.endDate);
                  const hasAgreement = _.get(agreement, 'signedAgreement.id');

                  return (
                    <tr key={agreement.id} className={classNames({ expired })}>
                      <td><CompanyNickname company={agreement.company} /></td>
                      <td>
                        { hasAgreement && <GoogleDriveLink className="margin-right" fileId={agreement.signedAgreement.fileId} /> }
                        <AgreementLink agreement={agreement}>{expired && '[expired] '}{agreement.title}</AgreementLink>
                      </td>
                      <td><ShortDate date={agreement.startDate} /></td>
                      <td><ShortDate date={agreement.endDate} /></td>
                      <td>{agreement.department}</td>
                      <td>{Agreement.getBillingTypes(agreement)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <Pager count={count} />
        </PageContent>
      </Page>
    );
  }
}

// Exports
export default connect((state) => ({
  agreements: state.agreements.agreements,
  fetching: state.agreements.fetching,
  count: state.agreements.count,
  query: state.location.query
}))(Agreements);
